<template>
  <!-- Panel lateral derecho, slide-in. Mismo lenguaje visual que ConfigureSubdivisionsModal. -->
  <aside v-show="visible"
    class="absolute right-0 top-0 bottom-0 w-80 flex flex-col bg-white border-l border-[#E4E6EB] shadow-lg z-10">

    <!-- HEADER estilo Meta: título + toggle-all + close -->
    <header class="flex items-center justify-between px-4 py-3 border-b border-[#E4E6EB] bg-white">
      <h2 class="text-base font-semibold text-gray-900">
        {{ $t('layers') }}
      </h2>

      <div class="flex items-center gap-1">
        <!-- Toggle visibilidad global -->
        <button
          @click="_ToggleAll"
          :title="$t('toggle_all_layers')"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-150">
          <b-icon :icon="allVisible ? 'eye' : 'eye-slash'" pack="fas" size="is-small" />
        </button>

        <!-- Cerrar -->
        <button
          @click="$emit('epc-close', true)"
          aria-label="Cerrar"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-150">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
          </svg>
        </button>
      </div>
    </header>

    <!-- LISTA scrollable -->
    <div class="flex-1 overflow-y-auto px-2 py-2 bg-white">
      <ul v-if="layers && layers.length" class="space-y-1">
        <li v-for="lyr in layers" :key="lyr.name"
            :class="[
              'flex items-center justify-between gap-2 px-2 py-1.5 rounded transition-colors duration-150',
              lyr.name === activeName
                ? 'bg-blue-50 ring-1 ring-blue-200'
                : (locked ? '' : 'hover:bg-gray-50'),
            ]">
          <!-- Cuadradito de color + nombre — click activa la capa para
               filtrar los clicks sobre el viewer (solo la activa responde).
               Si locked (workLayer en la URL), el click es no-op. -->
          <div :class="[
                 'flex items-center gap-2 min-w-0 flex-1',
                 locked ? 'cursor-default' : 'cursor-pointer',
               ]"
               @click="_ActivateLayer(lyr)">
            <span class="w-3 h-3 rounded-sm flex-shrink-0 border border-gray-200"
                  :style="{ backgroundColor: _GetCssColor(lyr.color) }"></span>
            <span class="text-sm truncate min-w-0 flex-1"
                  :class="lyr.name === activeName ? 'text-blue-900 font-semibold' : 'text-gray-700'"
                  :title="lyr.displayName || lyr.name">
              {{ lyr.displayName || lyr.name }}
            </span>
            <!-- Candado al lado del nombre cuando la capa está fijada por
                 workLayer en la URL — fuera del span truncado para que no
                 se recorte si el nombre es largo. -->
            <b-icon v-if="locked && lyr.name === activeName"
              pack="fas" icon="lock" size="is-small" class="text-blue-600 flex-shrink-0" />
          </div>

          <!-- Toggle visibilidad individual (independiente de activación). -->
          <button @click.stop="_ToggleLayer(lyr)"
            class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-150">
            <b-icon :icon="lyr.isVisible ? 'eye' : 'eye-slash'" pack="fas" size="is-small" />
          </button>
        </li>
      </ul>

      <!-- Empty state -->
      <p v-else class="text-sm text-gray-400 text-center py-6">
        {{ $t('no_layers_to_display') }}
      </p>
    </div>
  </aside>
</template>

<script>
export default {
  name: "LayersPanel",

  props: {
    // [{name, displayName, color, isVisible}] — el padre maneja la mutación
    layers:  { type: Array,   default: () => [] },
    // Controla v-show desde el padre. No usamos v-if para mantener el state del scroll al re-abrir.
    visible: { type: Boolean, default: false },
    // Nombre de la capa activa (la única que responde a clicks sobre el viewer).
    // null = todas las capas trackeables son clickeables (comportamiento default).
    activeName: { type: String, default: null },
    // Si true, la capa activa está fijada (típicamente porque viene en la URL
    // como `wl`) y el usuario no puede cambiarla.
    locked:     { type: Boolean, default: false },
  },

  computed: {
    /**
     * Estado del icono "toggle todas". Derivado del array para que siempre esté en sync
     * con el state real (no como el data interno `showAll` del legacy que se desincronizaba).
     */
    allVisible() {
      // Defensa contra null/undefined: aunque la prop tiene default [], el padre
      // puede pasar null explícitamente y entonces el default no aplica.
      const list = this.layers || []
      return list.length > 0 && list.every(l => l.isVisible)
    },
  },

  methods: {
    _ToggleLayer(layer) {
      // Solo emite. El padre es el único que muta el array (props down, events up).
      this.$emit('toggleLayer', layer, !layer.isVisible)
    },

    _ToggleAll() {
      this.$emit('toggleAll', !this.allVisible)
    },

    /**
     * Click sobre el nombre/swatch — activa la capa. Si ya estaba activa,
     * la desactiva (vuelve al modo "todas clickeables"). Si está lockada
     * (por workLayer/url) es no-op.
     */
    _ActivateLayer(layer) {
      if (this.locked) return
      const next = layer.name === this.activeName ? null : layer.name
      this.$emit('activate-layer', next)
    },

    _GetCssColor(value) {
      let s = value.toString(16)
      while (s.length < 6) s = '0' + s
      return '#' + s
    },
  },
}
</script>
